import { Platform, RevenueModel, EstimationOption, EstimationResult, ProjectDescription, SelectedFunctionality } from '@/types/estimation';
import { FUNCTIONALITIES } from './functionalities';

// Base prices by platform
const BASE_PRICES: Record<Platform, { min: number; max: number }> = {
  web: { min: 5000, max: 15000 },
  mobile: { min: 8000, max: 20000 },
  desktop: { min: 10000, max: 25000 },
  tablet: { min: 6000, max: 18000 },
  tv: { min: 12000, max: 30000 },
  watch: { min: 5000, max: 15000 },
};

// Revenue model multipliers (can stack if multiple selected)
const REVENUE_MODEL_MULTIPLIERS: Record<RevenueModel, number> = {
  subscription: 1.2,
  'in-app-purchase': 1.15,
  ads: 1.1,
  commission: 1.3,
  'one-time-payment': 1.0,
  freemium: 1.25,
  sponsorship: 1.1,
  other: 1.0,
};

// Available options
export const AVAILABLE_OPTIONS: EstimationOption[] = [
  {
    id: 'mobile-app',
    name: 'Application mobile native',
    description: 'Application iOS et Android native',
    price: 8000,
    category: 'platform',
  },
  {
    id: 'web-app',
    name: 'Application web responsive',
    description: 'Application web adaptée mobile et desktop',
    price: 5000,
    category: 'platform',
  },
  {
    id: 'admin-panel',
    name: 'Panneau d\'administration',
    description: 'Interface d\'administration complète',
    price: 3000,
    category: 'feature',
  },
  {
    id: 'payment-integration',
    name: 'Intégration paiement',
    description: 'Stripe, PayPal, ou autres solutions de paiement',
    price: 2000,
    category: 'feature',
  },
  {
    id: 'auth-system',
    name: 'Système d\'authentification avancé',
    description: 'OAuth, 2FA, gestion des rôles',
    price: 2000,
    category: 'feature',
  },
  {
    id: 'messaging',
    name: 'Système de messagerie',
    description: 'Chat en temps réel entre utilisateurs',
    price: 3000,
    category: 'feature',
  },
  {
    id: 'notifications',
    name: 'Notifications push',
    description: 'Notifications push et email',
    price: 1500,
    category: 'feature',
  },
  {
    id: 'analytics',
    name: 'Analytics et reporting',
    description: 'Tableaux de bord et statistiques avancées',
    price: 2500,
    category: 'feature',
  },
  {
    id: 'ai-integration',
    name: 'Intégration IA',
    description: 'Fonctionnalités basées sur l\'IA',
    price: 5000,
    category: 'feature',
  },
  {
    id: 'api-integration',
    name: 'Intégrations API tierces',
    description: 'Connexion avec services externes',
    price: 2000,
    category: 'feature',
  },
  {
    id: 'maintenance-3',
    name: 'Maintenance 3 mois',
    description: 'Support et maintenance pendant 3 mois',
    price: 1200,
    category: 'service',
  },
  {
    id: 'maintenance-6',
    name: 'Maintenance 6 mois',
    description: 'Support et maintenance pendant 6 mois',
    price: 2400,
    category: 'service',
  },
  {
    id: 'seo-optimization',
    name: 'Optimisation SEO',
    description: 'Optimisation pour les moteurs de recherche',
    price: 2000,
    category: 'service',
  },
  {
    id: 'design-system',
    name: 'Système de design personnalisé',
    description: 'Design unique et identité visuelle',
    price: 3000,
    category: 'service',
  },
];

export function calculateEstimation(
  platforms: Platform[],
  revenueModels: RevenueModel[],
  projectDescription: ProjectDescription,
  selectedFunctionalities: SelectedFunctionality[],
  selectedOptions: EstimationOption[] = []
): EstimationResult {
  // Safety checks
  if (!platforms || platforms.length === 0) {
    platforms = ['web'];
  }
  if (!revenueModels || revenueModels.length === 0) {
    revenueModels = ['other'];
  }
  if (!selectedFunctionalities) {
    selectedFunctionalities = [];
  }
  if (!selectedOptions) {
    selectedOptions = [];
  }
  
  // Calculate base price from all selected platforms
  // Sum up prices, but apply a discount for multiple platforms (10% discount per additional platform, max 30%)
  let totalMinPrice = 0;
  let totalMaxPrice = 0;
  
  platforms.forEach(platform => {
    const priceRange = BASE_PRICES[platform];
    totalMinPrice += priceRange.min;
    totalMaxPrice += priceRange.max;
  });
  
  // Apply discount for multiple platforms
  if (platforms.length > 1) {
    const discount = Math.min((platforms.length - 1) * 0.1, 0.3); // 10% per additional, max 30%
    totalMinPrice *= (1 - discount);
    totalMaxPrice *= (1 - discount);
  }
  
  const basePrice = (totalMinPrice + totalMaxPrice) / 2;
  
  // Apply revenue model multipliers (stack if multiple selected)
  // Use the highest multiplier as base, then add 5% for each additional model
  let multiplier = 1.0;
  if (revenueModels && revenueModels.length > 0) {
    const multipliers = revenueModels.map(rm => REVENUE_MODEL_MULTIPLIERS[rm]);
    multiplier = Math.max(...multipliers);
    // Add 5% for each additional revenue model (up to 20% total bonus)
    if (revenueModels.length > 1) {
      const additionalBonus = Math.min((revenueModels.length - 1) * 0.05, 0.2);
      multiplier += additionalBonus;
    }
  }
  
  const adjustedBasePrice = basePrice * multiplier;
  
  // Calculate functionalities price
  let functionalitiesPrice = 0;
  const functionalityNames: string[] = [];
  
  selectedFunctionalities.forEach(selectedFunc => {
    const func = FUNCTIONALITIES.find(f => f.id === selectedFunc.functionalityId);
    if (func) {
      functionalitiesPrice += func.basePrice;
      functionalityNames.push(func.name);
      
      // Add sub-choices prices
      if (func.subChoices && selectedFunc.selectedSubChoices) {
        selectedFunc.selectedSubChoices.forEach(subChoiceId => {
          const subChoice = func.subChoices?.find(sc => sc.id === subChoiceId);
          if (subChoice && subChoice.price) {
            functionalitiesPrice += subChoice.price;
          }
        });
      }
    }
  });
  
  // Calculate additional options price
  const optionsPrice = selectedOptions.reduce((sum, option) => sum + option.price, 0);
  
  // Calculate total
  const totalPrice = adjustedBasePrice + functionalitiesPrice + optionsPrice;
  const minPrice = totalMinPrice * multiplier + functionalitiesPrice + optionsPrice;
  const maxPrice = totalMaxPrice * multiplier + functionalitiesPrice + optionsPrice;
  
  // Generate technologies
  const technologies = generateTechnologies(platforms, revenueModels, projectDescription.techStack);
  
  // Generate timetable
  const timetable = generateTimetable(platforms, revenueModels, selectedFunctionalities.length);
  
  return {
    basePrice: adjustedBasePrice,
    minPrice: Math.round(minPrice),
    maxPrice: Math.round(maxPrice),
    platforms,
    revenueModels,
    projectDescription,
    functionalities: functionalityNames,
    technologies,
    timetable,
    selectedOptions,
    totalPrice: Math.round(totalPrice),
  };
}

// Functionalities are now handled directly from selectedFunctionalities

function generateTechnologies(platforms: Platform[], revenueModels: RevenueModel[], userTechStack?: string[]): string[] {
  const techs: string[] = [];
  
  // Safety checks
  if (!platforms || platforms.length === 0) {
    platforms = ['web'];
  }
  if (!revenueModels || revenueModels.length === 0) {
    revenueModels = ['other'];
  }
  
  // Use user's tech stack if provided, otherwise suggest defaults
  if (userTechStack && userTechStack.length > 0) {
    techs.push(...userTechStack);
  } else {
    if (platforms.includes('web')) {
      techs.push('React', 'TypeScript', 'Tailwind CSS');
    }
    
    if (platforms.includes('mobile') || platforms.includes('tablet')) {
      techs.push('React Native', 'Flutter');
    }
    
    if (platforms.includes('desktop')) {
      techs.push('Electron', 'Tauri');
    }
    
    if (platforms.includes('tv')) {
      techs.push('React Native TV', 'Android TV SDK');
    }
    
    if (platforms.includes('watch')) {
      techs.push('WatchKit', 'Wear OS SDK');
    }
    
    techs.push('Node.js', 'PostgreSQL', 'AWS/Vercel');
  }
  
  // Add payment-related technologies based on revenue models
  if (revenueModels.includes('subscription') || revenueModels.includes('in-app-purchase') || 
      revenueModels.includes('commission') || revenueModels.includes('one-time-payment')) {
    if (!techs.some(t => t.toLowerCase().includes('stripe'))) {
      techs.push('Stripe');
    }
  }
  
  if (revenueModels.includes('ads')) {
    techs.push('Google AdSense', 'AdMob');
  }
  
  if (revenueModels.includes('commission')) {
    if (!techs.some(t => t.toLowerCase().includes('redis'))) {
      techs.push('Redis', 'Webhooks');
    }
  }
  
  return techs;
}

function generateTimetable(
  platforms: Platform[],
  revenueModels: RevenueModel[],
  optionsCount: number
): EstimationResult['timetable'] {
  // Safety checks
  if (!platforms || platforms.length === 0) {
    platforms = ['web'];
  }
  if (typeof optionsCount !== 'number') {
    optionsCount = 0;
  }
  
  // Calculate base weeks based on number and complexity of platforms
  let baseWeeks = 8; // Default for web
  if (platforms.length > 1) {
    baseWeeks = 10 + (platforms.length - 1) * 2; // Add 2 weeks per additional platform
  } else if (platforms.includes('mobile') || platforms.includes('desktop') || platforms.includes('tv')) {
    baseWeeks = 10;
  } else if (platforms.includes('tablet') || platforms.includes('watch')) {
    baseWeeks = 9;
  }
  
  const complexityWeeks = Math.ceil(optionsCount / 3);
  const totalWeeks = baseWeeks + complexityWeeks;
  
  const phases = [
    {
      name: 'Phase 1: Conception',
      duration: '2-3 semaines',
      description: 'Analyse des besoins, wireframes, maquettes et architecture technique',
    },
    {
      name: 'Phase 2: Développement',
      duration: `${Math.ceil(totalWeeks * 0.6)}-${Math.ceil(totalWeeks * 0.7)} semaines`,
      description: 'Développement des fonctionnalités principales et intégrations',
    },
    {
      name: 'Phase 3: Tests et optimisations',
      duration: '2-3 semaines',
      description: 'Tests utilisateurs, corrections, optimisations de performance',
    },
    {
      name: 'Phase 4: Déploiement',
      duration: '1-2 semaines',
      description: 'Mise en production, formation et documentation',
    },
  ];
  
  return {
    phases,
    totalDuration: `${totalWeeks}-${totalWeeks + 2} semaines`,
  };
}

